/**
 * Encrypted record store — the codec under {@link defineSealedCollection}.
 *
 * A record is sealed with a SEA envelope before it hits GunDB and opened with
 * the reader's SEA pair, so peers holding the replicated data cannot read it.
 * This is what the public namespace needs when a record must be world-STORED
 * without being world-READABLE: Gun's user graph is write-protected but still
 * world-readable, so "put it under my own pub" is not privacy.
 *
 * The recipient set may be one pub (a private journal synced across your own
 * devices) or several (a record two parties share). One recipient is not a
 * special case — it is the same envelope with a shorter wrap list.
 *
 * Design:
 * - Each record is sealed into its own envelope for its recipient set. The
 *   sealer's pub is stored so a reader can fetch their epub and derive the
 *   pairwise secret.
 * - A sealed, signed, chained envelope is the ONLY stored shape. There is no
 *   plaintext fallback and no unsigned tier: a record in the world-writable
 *   public namespace that nobody can attribute is not worth storing, and
 *   tolerating the shape on read is what let one be written.
 * - Sealing therefore requires a SEA session. {@link toStored} throws without
 *   one rather than downgrading.
 */

import { onSessionReset, sessionPair, sessionPub } from './gunClient';
import { createPersistedMarkStore } from './persistedMarkStore';
import { GUN_STORAGE_PREFIX } from './gunConfig';
import type { SharedEncryptionEnvelope } from './envelopeTypes';
import {
  getEpub,
  sealEnvelope,
  openEnvelope,
  verifyEnvelopeSignature,
  envelopeChainHash,
} from './encryptionEnvelope';

export type RecipientRef = { pub: string; epub?: string };

/**
 * Who was allowed to seal a record: the pubs themselves, or a function handed
 * the DECRYPTED record so a caller whose party set lives inside the payload can
 * work it out.
 *
 * The function form exists because the answer is often not knowable before the
 * body is open — an engagement names its own parties, a chat thread names its
 * participants. This module used to reach into the payload for those fields
 * itself (`record.ownerPub`, `record.counterparty.kind === 'platform'`, a
 * registered resolver keyed on `record.engagementId`), which hardcoded one
 * app's domain model into the layer: any project whose records lack those
 * fields got `unresolved_parties` for every record it stored.
 *
 * Returning `null` means "cannot tell right now" — a cold read, an offline
 * relay — and is NOT the same as "nobody". Returning `[]` rejects.
 */
export type PartyResolver = (record: unknown) => string[] | null | Promise<string[] | null>;
export type PartySpec = string[] | PartyResolver;

/** The current user's SEA pair, or null when logged out. */
export const getMyPair = sessionPair;
const getMyPub = sessionPub;

/** What we write to a Gun node. One shape: a sealed envelope. */
export type StoredRecord = { id: string; enc: 1; sealerPub: string; env: string };

/** Thrown by {@link toStored} rather than downgrading a record to plaintext. */
export const RECIPIENTS_UNRESOLVED = 'recipients_unresolved';

/** Thrown by {@link toStored} when there is no SEA session to seal with. */
export const SEAL_REQUIRES_SESSION = 'seal_requires_session';

/**
 * Transition-chain memory: the latest envelope seen (read OR written) per
 * record id. A writer chains its next envelope to this (seq + 1, prevHash);
 * a reader rejects any envelope older than it (replay of a superseded state).
 *
 * PERSISTED, because the guarantee is worthless otherwise. Held only in memory,
 * the high-water mark died with the tab: an attacker who re-put a superseded
 * envelope (rolling a `disputed` payment back to `declared`, say) simply had to
 * wait for the victim to reload, at which point the stale envelope was the
 * first one seen and became the new anchor. The mark is written to IndexedDB
 * and reloaded on boot so it survives that.
 *
 * The store is keyed per account: one user's view of "latest" must never gate
 * another's reads on a shared device.
 */
const latestEnvelopes = createPersistedMarkStore(`${GUN_STORAGE_PREFIX}_envelope_chain_`);

/**
 * Highest `seq` seen in a *validly signed* envelope for a record, whether or not
 * its sealer could be confirmed as a party.
 *
 * Split from {@link latestEnvelopes} because the two marks answer different
 * questions and conflating them broke one of them either way:
 *
 * - The gate mark (`latestEnvelopes`) decides what to REJECT, so it must only
 *   advance on a fully verified envelope — otherwise a stranger with any valid
 *   keypair could seal a high-seq record and permanently block the genuine one.
 * - This mark decides what seq to ALLOCATE on the next write. Holding it to the
 *   same standard meant a counterparty record read as `unresolved_parties`
 *   never advanced anything, so our next write reused a seq the other side had
 *   already passed — and *they* then rejected our write as `stale_replay`.
 *
 * Not persisted: it only has to beat what this session has actually seen, and
 * the gate mark (which is persisted) is its floor on a cold start.
 */
const observedSeq = new Map<string, number>();

/**
 * Load the persisted chain marks for `pub`. Call once a session is established;
 * safe to call repeatedly. See {@link createPersistedMarkStore} for the
 * per-account, memoized-load, forward-merge invariants.
 *
 * `observedSeq` is dropped alongside a switch: it is per-account state too,
 * even though it is never persisted.
 */
export function loadEnvelopeChainMemory(pub: string): Promise<void> {
  if (pub && loadedChainPub && loadedChainPub !== pub) observedSeq.clear();
  if (pub) loadedChainPub = pub;
  return latestEnvelopes.load(pub);
}
let loadedChainPub: string | null = null;

async function rememberEnvelope(id: string, envelope: SharedEncryptionEnvelope): Promise<void> {
  if (typeof envelope.seq !== 'number') return;
  rememberObserved(id, envelope.seq);
  // Advance-only, with the hash computed before the store is touched: an await
  // between read and set let two concurrent verifications interleave so the
  // lower seq wrote last, regressing the mark and reopening the replay window.
  latestEnvelopes.advance(id, { seq: envelope.seq, hash: await envelopeChainHash(envelope) });
}

/** Note a validly signed seq for write-side allocation only — see {@link observedSeq}. */
function rememberObserved(id: string, seq: number): void {
  if (typeof seq !== 'number' || !Number.isFinite(seq)) return;
  const known = observedSeq.get(id) ?? 0;
  if (seq > known) observedSeq.set(id, seq);
}

/** The seq a fresh write for `id` must exceed. */
function highestKnownSeq(id: string): number {
  return Math.max(latestEnvelopes.get(id)?.seq ?? 0, observedSeq.get(id) ?? 0);
}

/** Reset chain memory (logout / account switch / tests). */
export function resetEnvelopeChainMemory(): void {
  latestEnvelopes.reset();
  observedSeq.clear();
  loadedChainPub = null;
}

// Chain memory is per-account state: keep it across an account switch and the
// old session's view of "latest" would wrongly reject the new account's reads —
// and `schedulePersist` would write the old account's marks under the new
// account's key. onSessionReset, not a logout hook: a login over a live session
// switches accounts without ever calling gunLogout.
try {
  onSessionReset(resetEnvelopeChainMemory);
} catch {
  /* non-browser test environments may lack the client */
}

/**
 * Serialize a record for storage, sealing it for `recipients`.
 *
 * Throws rather than degrading, in both directions: with no SEA session there is
 * nothing to seal or sign with, and with an unresolvable recipient set there is
 * nobody to seal it for. Writing a commercial record unencrypted and unsigned
 * into the world-writable public graph is worse than failing loudly.
 */
export async function toStored<T>(
  id: string,
  record: T,
  recipients: RecipientRef[],
): Promise<StoredRecord> {
  const pair = getMyPair();
  const sealerPub = getMyPub();
  if (!pair || !sealerPub) throw new Error(SEAL_REQUIRES_SESSION);
  // The next envelope chains onto the newest mark we know of, so the persisted
  // marks have to be in hand before allocating `seq` — otherwise a write after
  // a reload restarts the chain at 1 and every reader sees a rollback.
  await loadEnvelopeChainMemory(sealerPub);
  if (recipients.length === 0) throw new Error(RECIPIENTS_UNRESOLVED);
  // Resolve epubs for any recipient that did not supply one. A recipient whose
  // epub cannot be resolved is a HARD failure, not a skip: silently dropping
  // them writes a record the counterparty can never decrypt, with no signal to
  // either side — the write "succeeds" and the relationship is broken.
  const resolved: Array<{ pub: string; epub: string }> = [];
  for (const r of recipients) {
    const epub = r.epub ?? (await getEpub(r.pub));
    if (!epub && r.pub !== sealerPub) throw new Error(RECIPIENTS_UNRESOLVED);
    if (epub) resolved.push({ pub: r.pub, epub });
  }
  // Always include the sealer as a recipient so they can read their own record.
  if (!resolved.some((r) => r.pub === sealerPub)) {
    const myEpub = (pair as any).epub as string | undefined;
    if (myEpub) resolved.push({ pub: sealerPub, epub: myEpub });
  }
  if (resolved.length === 0) throw new Error(RECIPIENTS_UNRESOLVED);
  // Chain to the latest envelope this client has seen for the record. First
  // write starts at seq 1.
  //
  // `seq` comes from the COMBINED mark (see observedSeq): a record we could read
  // but not attribute to a party still moved the chain forward for everyone
  // else, so allocating from the gate mark alone produced a write the
  // counterparty rejected as a rollback.
  //
  // `prevHash` is only sent when the gate mark IS the predecessor. Sending a
  // hash from further back would be a claim the reader cannot satisfy: they
  // check `prevHash` against their own mark for seq-1, so a mismatched one
  // reads as a fork. A gap is unverifiable either way, so it says so with null.
  const prev = latestEnvelopes.get(id);
  const highest = highestKnownSeq(id);
  const envelope = await sealEnvelope({
    recordId: id,
    seq: highest + 1,
    prevHash: prev && prev.seq === highest ? prev.hash : null,
    payload: record,
    ownerPair: pair,
    recipients: resolved,
  });
  await rememberEnvelope(id, envelope);
  return { id, enc: 1, sealerPub, env: JSON.stringify(envelope) };
}

/** Who is allowed to have sealed this record, or null when it can't be resolved. */
async function allowedSealers(record: unknown, parties?: PartySpec): Promise<string[] | null> {
  if (!parties) return null;
  if (Array.isArray(parties)) return parties.length > 0 ? parties : null;
  try {
    return await parties(record);
  } catch {
    return null;
  }
}

export type ReadStoredResult<T> = {
  record: T | null;
  /** True only for an envelope whose signature verified against a party pub. */
  verified: boolean;
  /** Why the record is not verified (or why it was rejected). */
  reason?:
    | 'unsigned'
    | 'unresolved_parties'
    | 'bad_signature'
    | 'sealer_not_party'
    /** A payload field contradicts the envelope carrying it. See the `check` hook. */
    | 'binding_mismatch'
    /** Decrypted, but not the shape this collection stores. See defineSealedCollection. */
    | 'invalid_body'
    | 'envelope_mismatch'
    | 'chain_fork'
    | 'unchained'
    | 'stale_replay'
    | 'unanchored'
    | 'unreadable';
};

/**
 * Open a stored record, reporting whether its provenance could be verified.
 *
 * A sealed record is trusted only when its signature verifies against the
 * claimed `sealerPub` AND that pub is one of the record's parties —
 * otherwise anyone could overwrite a shared node with a forged envelope. There
 * is no readable-but-unverified tier for a missing signature or a plaintext
 * body: both are rejections.
 *
 * `opts.nodeId` is the Gun key the body was READ FROM, and it is what anchors
 * the record to its location. Without it the only available anchor is `data.id`
 * — a field of the same attacker-supplied blob as the envelope, so a signed
 * envelope for record A could be copied wholesale onto node B (with `id` set to
 * "A" to match) and every check still passed. A record with no `nodeId` is
 * still readable, but never `verified`, and never advances the chain memory.
 */
export async function readStored<T>(
  data: any,
  opts?: { parties?: PartySpec; nodeId?: string },
): Promise<ReadStoredResult<T>> {
  if (!data || typeof data !== 'object') return { record: null, verified: false, reason: 'unreadable' };
  // Encrypted envelope.
  if (data.enc === 1 && typeof data.env === 'string' && typeof data.sealerPub === 'string') {
    const pair = getMyPair();
    const myPub = getMyPub();
    if (!pair || !myPub) return { record: null, verified: false, reason: 'unreadable' };
    // Rollback-replay detection compares against the persisted high-water mark,
    // so it has to be loaded before the comparison below — not after the first
    // stale envelope has already been accepted and become the anchor.
    await loadEnvelopeChainMemory(myPub);
    let envelope: SharedEncryptionEnvelope;
    try {
      envelope = JSON.parse(data.env) as SharedEncryptionEnvelope;
    } catch {
      return { record: null, verified: false, reason: 'unreadable' };
    }
    // Fast path: records we sealed use our own epub — no network lookup needed.
    const sealerEpub =
      data.sealerPub === myPub
        ? ((pair as any).epub as string | undefined) ?? (await getEpub(data.sealerPub))
        : await getEpub(data.sealerPub);
    if (!sealerEpub) return { record: null, verified: false, reason: 'unreadable' };
    // Unsigned is a rejection, not a tier. An envelope nobody signed is an
    // envelope anybody could have written.
    if (!envelope.signature) return { record: null, verified: false, reason: 'unsigned' };
    if (!(await verifyEnvelopeSignature(envelope, data.sealerPub))) {
      return { record: null, verified: false, reason: 'bad_signature' };
    }
    // Transition chain: the signature covers recordId + seq + prevHash. A signed
    // envelope for a different node is a cross-node replay; one with a lower seq
    // than we have already seen is a rollback replay of an old state (re-putting
    // a superseded `declared` over a `disputed`); one that contradicts the mark
    // it claims to follow is a fork.
    //
    // The chain fields are REQUIRED, and checked here rather than trusted from
    // the type: this value is parsed from an untrusted node body. Omitting
    // `recordId` used to skip this whole block — and because JSON.stringify
    // drops undefined keys, the signature over an envelope without one still
    // verified. A sealer could therefore opt out of replay protection entirely
    // by leaving the field off.
    if (typeof envelope.recordId !== 'string' || !envelope.recordId) {
      return { record: null, verified: false, reason: 'unchained' };
    }
    if (typeof envelope.seq !== 'number' || !Number.isFinite(envelope.seq)) {
      return { record: null, verified: false, reason: 'unchained' };
    }

    // Anchoring is checked here, before the record is even opened: it depends
    // only on where the body was read from, not on who sealed it.
    const nodeId = typeof opts?.nodeId === 'string' && opts.nodeId ? opts.nodeId : null;
    if (nodeId && envelope.recordId !== nodeId) {
      return { record: null, verified: false, reason: 'envelope_mismatch' };
    }
    const record = await openEnvelope<T>(envelope, myPub, pair, sealerEpub);
    if (record == null) return { record: null, verified: false, reason: 'unreadable' };
    const parties = await allowedSealers(record, opts?.parties);
    if (parties && !parties.includes(data.sealerPub)) {
      return { record: null, verified: false, reason: 'sealer_not_party' };
    }
    // Narrowing, not just a guard: everything below needs a real node id.
    if (!nodeId) return { record, verified: false, reason: 'unanchored' };
    if (!parties) {
      // Signature checked out, sealer unconfirmed. The gate mark must NOT move
      // (a stranger's valid keypair would block the genuine record) but the
      // write-side mark must, or our next write reuses a seq the counterparty
      // has already passed and they reject it as a rollback.
      //
      // Bounded, because "unconfirmed" includes strangers: anyone can seal a
      // signature-valid envelope to this reader's public epub with an absurd
      // seq and inflate every future allocation. A genuine counterparty's chain
      // only ever runs ahead by the states written while this device was not
      // looking, so a cap far above any real burst keeps the legitimate flow
      // and strips the stranger's leverage.
      //
      // Capped against the GATE mark (which only verified envelopes advance),
      // not the combined mark: a cap relative to observedSeq itself lets
      // repeated stranger envelopes ratchet the allocation up +100 at a time
      // without bound.
      if (envelope.seq <= (latestEnvelopes.get(nodeId)?.seq ?? 0) + 100) {
        rememberObserved(nodeId, envelope.seq);
      }
      return { record, verified: false, reason: 'unresolved_parties' };
    }

    // Transition chain, evaluated LAST — only for an envelope that is signed by
    // a confirmed party of this record. Running it earlier meant a stranger's
    // envelope was compared against the chain at all: it was rejected either
    // way, but as `chain_fork` rather than `sealer_not_party`, and a non-party
    // has no business influencing how a party's chain reads.
    //
    // A lower seq than we have seen is a rollback replay of an old state
    // (re-putting a superseded `declared` over a `disputed`); the same seq with
    // different content is the same attack one step sideways; a direct
    // successor that does not name the envelope we hold is a fork.
    const known = latestEnvelopes.get(nodeId);
    if (known) {
      if (envelope.seq < known.seq) {
        return { record: null, verified: false, reason: 'stale_replay' };
      }
      if (envelope.seq === known.seq) {
        if ((await envelopeChainHash(envelope)) !== known.hash) {
          return { record: null, verified: false, reason: 'chain_fork' };
        }
      } else if (envelope.seq === known.seq + 1) {
        // A gap (seq > known.seq + 1) is legitimate — states written while this
        // device was offline are simply not in hand.
        if (envelope.prevHash != null) {
          if (envelope.prevHash !== known.hash) {
            return { record: null, verified: false, reason: 'chain_fork' };
          }
        } else {
          // A null prevHash on the DIRECT successor of a state we hold. It can
          // be honest — a writer that learned the seq from an unattributed
          // envelope holds no hash to chain to — but it is also exactly how a
          // dishonest party would evade the fork check above. Split the
          // difference: the record stays readable, but it is not `verified` and
          // the gate mark below must NOT advance, so it can never displace the
          // chained state this client already holds.
          return { record, verified: false, reason: 'unchained' };
        }
      }
    }

    // Only a fully verified envelope advances the chain memory — otherwise a
    // forged high-seq envelope could block the genuine record.
    await rememberEnvelope(nodeId, envelope);
    return { record, verified: true };
  }
  return { record: null, verified: false, reason: 'unreadable' };
}

/**
 * Should a live subscription act on this raw body, or ignore it?
 *
 * Sealed records live in the PUBLIC namespace, which Gun does not
 * access-control. The envelope buys confidentiality (only recipients can read)
 * and integrity (only a party's signature verifies) — it buys nothing at all
 * against *erasure*: any peer can `put(null)` on `engagements/<id>`,
 * `agreementsV2/<id>` or `payments/<id>` and tombstone a record it cannot even
 * read. Moving these into owner space with a public index is the real fix and
 * is a schema change; until then, clients refuse to act on the tombstone.
 *
 * That is safe here because these records are append-only by construction:
 * nothing in the app deletes an engagement, agreement or payment — lifecycle is
 * expressed as new states inside the sealed payload. So a `null` body is never
 * something we asked for, and a subscriber that already holds a good copy
 * should keep it rather than let a stranger blank the screen.
 */
export function isSealedTombstone(raw: unknown): boolean {
  return raw === null || raw === undefined;
}

/**
 * Open a stored record (encrypted or plaintext). Returns null if unreadable.
 *
 * Provenance is discarded, so pass `opts.nodeId` and use {@link readStored}
 * instead wherever the answer drives anything more than display.
 */
export async function fromStored<T>(
  data: any,
  opts?: { parties?: PartySpec; nodeId?: string },
): Promise<T | null> {
  const { record } = await readStored<T>(data, opts);
  return record;
}

/**
 * A record plus whether its provenance verified — for services that must refuse
 * to act on a record they cannot attribute to a party.
 */
export type VerifiedRecord<T> = { record: T | null; verified: boolean; reason?: ReadStoredResult<T>['reason'] };

/** Provenance of a record that was readable, keyed by record id for the UI. */
export type RecordProvenance = Pick<ReadStoredResult<unknown>, 'verified' | 'reason'>;
export type ProvenanceMap = Record<string, RecordProvenance>;

// provenanceNote (the human wording of these reasons) is UI copy and lives
// with the app: src/features/engagements/utils/provenanceNote.ts.
